import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableModelFilterApplied,
  fetchAllModels,
  fetchModelFilters,
  setModelLimit,
  setModelPage,
  setModelSortBy,
  setModelSortField,
  updateModelFilters,
} from '../../../redux/slices/ModelSlice';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/device-hub';
import AddModelDrawer from '../../ev-device-hub/components/drawers/AddModelDrawer';
import EditModelDrawer from '../../ev-device-hub/components/drawers/EditModelDrawer';
// ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout, PR #1130/#1143).
// Re-enable by uncommenting this once backend security endpoints/fields ship.
// import { formatProfiles } from '../../ev-device-hub/components/security/securityProfile';
import '../../ev-device-hub/components/security/security.scss';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Local models list component — displays models belonging to the current tenant.
 * @returns {React.JSX.Element} Local models table
 */
function Local() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['deviceHub', 'sharedComponent']);
  const {
    models,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.model);

  const [modelDrawerOpen, setModelDrawerOpen] = useState(false);
  const [editModelOpen, setEditModelOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: null,
    header: false,
    content: null,
    notificationModal: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllModels({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllModels({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableModelFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setModelSortBy(sortData.sortBy));
    dispatch(setModelSortField(sortData.sortField));
    dispatch(
      fetchAllModels({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setModelPage(value.page));
    if (value.limit !== undefined) dispatch(setModelLimit(value.limit));
    dispatch(
      fetchAllModels({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes model data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllModels({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Calls the API to delete a model
   * @param {string} modelId - The ID of the model to delete
   */
  const handleConfirmDeleteModel = (modelId: string) => {
    deviceApi
      .deleteModel(modelId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title={t('modelDeletedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                handleRefresh();
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Triggers the delete confirmation warning modal for a model
   * @param {string} modelId - The ID of the model
   */
  const handleDeleteModelClick = (modelId: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={modelId}
          title={t('deleteModelConfirmation')}
          description={t('deleteModelConfirmationDesc')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id) => id && handleConfirmDeleteModel(id)}
        />
      ),
      notificationModal: true,
    });
  };

  const column = [
    {
      label: t('modelName'),
      accessorKey: 'name',
    },
    {
      label: t('brandName'),
      accessorKey: 'brandId',
      /**
       * Renders brand name
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>
          {currentRow.brand?.name ||
            currentRow.brandId?.name ||
            currentRow.brandName ||
            currentRow.brandId ||
            '-'}
        </span>
      ),
    },
    {
      label: t('media'),
      accessorKey: 'imageUrls',
      /**
       * Renders model media preview
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => {
        const image =
          currentRow.imageUrl ||
          currentRow.imageUrls?.[0]?.signedUrl ||
          currentRow.imageUrls?.[0]?.url;
        return image ? (
          <div className="model-media-cell">
            <div className="model-media-wrapper">
              <img
                src={image}
                alt={currentRow.name || t('model')}
                className="model-media-thumbnail"
              />
            </div>
          </div>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      label: t('deviceCount'),
      accessorKey: 'deviceCount',
      sortAllow: true,
      /**
       * Renders device count
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.deviceCount || 0}</span>,
    },
    {
      label: t('diagnosticProtocolTypes', 'Diagnostic protocaltypes'),
      accessorKey: 'diagnosticProtocolTypes',
      /**
       * Renders diagnostic protocol types as a comma-separated list
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <p>
          {Array.isArray(currentRow.diagnosticProtocolTypes) && currentRow.diagnosticProtocolTypes.length > 0
            ? currentRow.diagnosticProtocolTypes.join(', ')
            : '-'}
        </p>
      ),
    },
    // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
    // PR #1130/#1143). Re-enable by uncommenting this column once backend security
    // endpoints/fields ship.
    /*
    {
      label: t('security', 'Security'),
      accessorKey: 'security',
      sortAllow: false,
      customData: (currentRow: any) => {
        const profiles = formatProfiles(currentRow.security);
        return profiles ? (
          <p>{profiles}</p>
        ) : (
          <span className="sp-badge">{t('notProfiled', 'Not profiled')}</span>
        );
      },
    },
    */
    {
      label: t('actions'),
      accessorKey: 'actions',
      sortAllow: false,
      columnWidth: 80,
      sticky: 'right' as const,
      /**
       * Renders row action buttons
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <div className="action-buttons-wrap">
          <div
            role="presentation"
            className="action-button"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedModel(currentRow);
              setEditModelOpen(true);
            }}>
            <EditIcon className="icon edit" />
          </div>
          <div
            role="presentation"
            className="action-button"
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteModelClick(currentRow._id);
            }}>
            <DeleteIcon className="icon delete" />
          </div>
        </div>
      ),
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo', 'S.No'), accessorKey: '__serial__' },
    {
      label: t('modelName'),
      accessorKey: 'name',
    },
    {
      label: t('brandName'),
      accessorKey: 'brandId',
    },
    {
      label: t('deviceCount'),
      accessorKey: 'deviceCount',
    },
    {
      label: t('diagnosticProtocolTypes', 'Diagnostic protocaltypes'),
      accessorKey: 'diagnosticProtocolTypes',
    },
    // SECURITY PROFILES DISABLED — backend not deployed yet, see column definitions above.
    /*
    {
      label: t('security', 'Security'),
      accessorKey: 'security',
    },
    */
    {
      label: t('createdAt', 'Created At'),
      accessorKey: 'createdAt',
    },
    {
      label: t('updatedAt', 'Last Updated'),
      accessorKey: 'updatedAt',
    },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function to export data in PDF or CSV format
   * @param {string} type - The export format type ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: Record<string, any> = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    deviceApi
      .exportModels(removeEmptyKeys(payload))
      .then((response: any) => {
        const rawList = response?.data?.results || response?.data || response?.results || [];
        const exportDataList = Array.isArray(rawList) ? rawList : [];

        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = exportDataList.map((row: any, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return String(index + 1);

              case 'name':
                return row?.name || '-';

              case 'brandId':
                return (
                  row?.brand?.name ||
                  row?.brandId?.name ||
                  row?.brandName ||
                  (typeof row?.brandId === 'string' ? row.brandId : '-')
                );

              case 'deviceCount':
                return row?.deviceCount !== undefined && row?.deviceCount !== null
                  ? String(row.deviceCount)
                  : '0';

              case 'diagnosticProtocolTypes':
                return Array.isArray(row?.diagnosticProtocolTypes) && row.diagnosticProtocolTypes.length > 0
                  ? row.diagnosticProtocolTypes.join(', ')
                  : '-';

              // SECURITY PROFILES DISABLED — backend not deployed yet, see imports above.
              // case 'security':
              //   return formatProfiles(row?.security) || t('notProfiled', 'Not profiled');

              case 'createdAt':
                return row?.createdAt ? ConvertUTCtoDateTime(row.createdAt) : '-';

              case 'updatedAt':
                return row?.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : '-';

              default:
                return row?.[col.accessorKey] ?? '-';
            }
          }),
        );

        setExportLoader(false);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'models-list.csv');
          } else {
            exportPDF(headers, rows, 'models-list.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  return (
    <>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />

      <CustomTable
        dataKey="ModelManagement"
        header={column}
        data={models}
        serialNo
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        height={310}
        created
        lastUpdated
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={handlePageLimitChange}
        sizeChangeButtonRequired
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        filters={filters}
        filtersToDispatch={updateModelFilters}
        filterListLoading={isFilterUpdated}
        filterListToDispatch={fetchModelFilters}
        actionButton
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
        refershCurrentPages={handleRefresh}
        onHiddenColumnsChange={setHiddenColumns}
        rightTopContent={
          <div className="table-bottom-button-list">
            {permissionCheck('devicesModelsAdd') && (
              <div className="table-button-item">
                <MyButton
                  label={t('addModel')}
                  buttonType="submit"
                  onClickFunc={() => setModelDrawerOpen(true)}
                />
              </div>
            )}
          </div>
        }
      />

      <AddModelDrawer
        open={modelDrawerOpen}
        onClose={() => setModelDrawerOpen(false)}
        onModelCreated={() => {
          setModelDrawerOpen(false);
          handleRefresh();
        }}
      />

      <EditModelDrawer
        open={editModelOpen}
        modelData={selectedModel}
        onClose={() => {
          setEditModelOpen(false);
          setSelectedModel(null);
        }}
        onModelUpdated={() => {
          setEditModelOpen(false);
          setSelectedModel(null);
          handleRefresh();
        }}
      />
    </>
  );
}

export default Local;
